import { Form, Input, Spin, notification } from "antd";
import { useEffect, useState } from "react";
import styled from "styled-components";
import BbotButton from "top-component-library/BbotButton";
import BbotModal from "top-component-library/BbotModal";
import { generalErrorAlert, isFeatureEnabledById } from "util/Utils";

import {
  buildInitialConfig,
  buildSavePayload,
  fetchDeviceProfileModalData,
  mergeEditedConfig,
  saveDeviceProfile,
} from "../modalUtils";
import AccessControlsSection from "../sections/AccessControlsSection";
import CustomKdsTicketColorsSection from "../sections/CustomKdsTicketColorsSection";
import DefaultPreferencesSection from "../sections/DefaultPreferencesSection";
import MembershipChecklistSection from "../sections/MembershipChecklistSection";
import NotificationsSection from "../sections/NotificationsSection";
import StatusChecklistSection from "../sections/StatusChecklistSection";
import UserPermissionsSection from "../sections/UserPermissionsSection";
import { CustomKdsTicketColors, DeviceProfileConfig, DeviceProfileModalData } from "../types";

interface SelectedCustomer {
  customer_id: string;
  enabled_features?: string[];
}

interface DeviceProfileModalProps {
  visible: boolean;
  config: DeviceProfileConfig | null; // null => adding a new profile
  selectedCustomer?: SelectedCustomer;
  onClose: () => void;
  onSaved: () => void;
}

const DeviceProfileModal = ({ visible, config, selectedCustomer, onClose, onSaved }: DeviceProfileModalProps) => {
  const [form] = Form.useForm();
  const customerId = selectedCustomer?.customer_id || "";
  const serviceRequestsEnabled = isFeatureEnabledById("service_requests", selectedCustomer);

  const [modalData, setModalData] = useState<DeviceProfileModalData | null>(null);
  const [initialConfig, setInitialConfig] = useState<DeviceProfileConfig | null>(null);
  // Tracks the profile id across saves. Undefined for a new profile until the first save returns
  // one, after which repeated (non-closing) saves update that row instead of creating duplicates.
  const [currentId, setCurrentId] = useState<string | undefined>(undefined);
  const [colorMethods, setColorMethods] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showLocationsOverview, setShowLocationsOverview] = useState(false);
  const [showCreateOrder, setShowCreateOrder] = useState(false);
  const [showStationsOverview, setShowStationsOverview] = useState(false);
  const [showKdsView, setShowKdsView] = useState(false);
  const [hasStations, setHasStations] = useState(false);
  const [hasKdsStations, setHasKdsStations] = useState(false);

  useEffect(() => {
    if (!visible) return undefined;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const data = await fetchDeviceProfileModalData(customerId);
        if (cancelled) return;
        const initial = buildInitialConfig(config);
        // Match the AngularJS controller: non-USD customers cannot use the card readers.
        if (!data.allowCc) {
          initial.use_stripe_chip_reader = false;
          initial.allow_manual_card_entry = false;
        }
        setModalData(data);
        setInitialConfig(initial);
        setCurrentId(initial.id);
        setColorMethods(Object.keys(initial.custom_kds_ticket_colors || {}));
        setShowLocationsOverview(!!initial.show_locations_overview);
        setShowCreateOrder(!!initial.show_create_order);
        setShowStationsOverview(!!initial.show_stations_overview);
        setShowKdsView(!!initial.show_kds_view);
        setHasStations((initial.stations || []).length > 0);
        setHasKdsStations((initial.kds_stations || []).length > 0);
        form.resetFields();
        form.setFieldsValue(initial);
      } catch (error) {
        if (!cancelled) {
          generalErrorAlert(error, "Trouble loading the device profile editor. Please try again.", true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [visible, config]); // eslint-disable-line react-hooks/exhaustive-deps

  // AntD 4.x has no Form.useWatch, so mirror the reveal-gate flags and the two "nothing selected"
  // warnings to component state on every change.
  const handleValuesChange = (_changed: Partial<DeviceProfileConfig>, all: DeviceProfileConfig) => {
    setShowLocationsOverview(!!all.show_locations_overview);
    setShowCreateOrder(!!all.show_create_order);
    setShowStationsOverview(!!all.show_stations_overview);
    setShowKdsView(!!all.show_kds_view);
    setHasStations((all.stations || []).length > 0);
    setHasKdsStations((all.kds_stations || []).length > 0);
  };

  const handleAddColorMethod = (method: string) => {
    if (!method || colorMethods.includes(method)) return;
    const existing: CustomKdsTicketColors = form.getFieldValue("custom_kds_ticket_colors") || {};
    form.setFieldsValue({ custom_kds_ticket_colors: { ...existing, [method]: "#000000" } });
    setColorMethods((methods) => [...methods, method]);
  };

  const handleRemoveColorMethod = (method: string) => {
    setColorMethods((methods) => methods.filter((current) => current !== method));
  };

  const handleSave = async (close: boolean) => {
    if (!initialConfig) return;
    setSaving(true);
    try {
      const edited = form.getFieldsValue(true) as Partial<DeviceProfileConfig>;
      // Only keep colors for methods currently shown so removed rows are dropped on save.
      const editedColors: CustomKdsTicketColors = (edited.custom_kds_ticket_colors as CustomKdsTicketColors) || {};
      const customColors: CustomKdsTicketColors = {};
      colorMethods.forEach((method) => {
        customColors[method] = editedColors[method] || "#000000";
      });
      const merged: DeviceProfileConfig = {
        ...mergeEditedConfig(initialConfig, edited, customColors),
        id: currentId,
      };
      const res = await saveDeviceProfile(buildSavePayload(merged, customerId));
      const savedId: string | undefined = res?.data?.id;
      if (savedId) setCurrentId(savedId);
      notification.success({ message: "Device profile saved." });
      onSaved();
      if (close) onClose();
    } catch (error) {
      generalErrorAlert(error, "Trouble saving the device profile. Please try again.", true);
    } finally {
      setSaving(false);
    }
  };

  const locationOptions = (modalData?.locations || []).map((location) => ({
    label: location.locationName,
    value: location.locationId,
  }));
  const stationOptions = (modalData?.stations || []).map((station) => ({
    label: station.station_name,
    value: station.id,
  }));
  const menuOptions = (modalData?.menus || []).map((menu) => ({ label: menu.menuName, value: menu.menuId }));

  return (
    <BbotModal
      title={`${config ? "Edit" : "Add"} Device Profile`}
      visible={visible}
      onCancel={onClose}
      width={720}
      bodyStyle={{ maxHeight: "70vh", overflowY: "auto" }}
      footer={[
        <BbotButton key="cancel" onClick={onClose}>
          Cancel
        </BbotButton>,
        <BbotButton key="save" type="primary" loading={saving} onClick={() => handleSave(false)}>
          Save
        </BbotButton>,
        <BbotButton key="save-close" type="primary" loading={saving} onClick={() => handleSave(true)}>
          Save and Close
        </BbotButton>,
      ]}
    >
      <Spin spinning={loading}>
        {modalData && (
          <Form form={form} layout="vertical" onValuesChange={handleValuesChange}>
            <Form.Item
              name="config_name"
              label="Profile Name"
              rules={[{ required: true, message: "Profile name is required." }]}
            >
              <Input id="dp-config-name" />
            </Form.Item>
            <Form.Item
              name="description"
              label="Description"
              rules={[{ required: true, message: "Description is required." }]}
            >
              <Input id="dp-description" />
            </Form.Item>

            <AccessControlsSection
              allowCc={modalData.allowCc}
              locationOptions={locationOptions}
              showLocationsOverview={showLocationsOverview}
              showCreateOrder={showCreateOrder}
              showStationsOverview={showStationsOverview}
              showKdsView={showKdsView}
              hasStations={hasStations}
              hasKdsStations={hasKdsStations}
            />
            <SectionSpacer />
            <UserPermissionsSection />
            <SectionSpacer />
            <CustomKdsTicketColorsSection
              colorMethods={colorMethods}
              fulfillmentMethodPrettyNames={modalData.fulfillmentMethodPrettyNames}
              onAddMethod={handleAddColorMethod}
              onRemoveMethod={handleRemoveColorMethod}
            />
            <SectionSpacer />
            <DefaultPreferencesSection />
            <SectionSpacer />
            <StatusChecklistSection
              id="dp-additional-closed-statuses"
              label="Additional Closed Statuses"
              name="additional_closed_statuses"
              tooltip={
                <>
                  Note: Orders with the selected statuses will not show on the <b>&quot;Current Orders&quot;</b> screen.
                  (Terminal 3.2+)
                </>
              }
            />
            <SectionSpacer />
            <NotificationsSection serviceRequestsEnabled={serviceRequestsEnabled} />
            <SectionSpacer />
            <MembershipChecklistSection
              id="dp-locations"
              label="Locations"
              name="locations"
              options={locationOptions}
              form={form}
            />
            <SectionSpacer />
            <MembershipChecklistSection
              id="dp-stations"
              label="Stations"
              name="stations"
              options={stationOptions}
              form={form}
            />
            {showKdsView && (
              <>
                <SectionSpacer />
                <MembershipChecklistSection
                  id="dp-kds-stations"
                  label="KDS Stations"
                  name="kds_stations"
                  options={stationOptions}
                  form={form}
                />
              </>
            )}
            <SectionSpacer />
            <MembershipChecklistSection id="dp-menus" label="Menus" name="menus" options={menuOptions} form={form} />
          </Form>
        )}
      </Spin>
    </BbotModal>
  );
};

export default DeviceProfileModal;

const SectionSpacer = styled.div`
  height: 16px;
`;
